import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { AlertCircle, Loader2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { safeReturnTo } from "../utils/loginTargets";
import { detectSurface } from "../utils/surfaceDetection";

/**
 * Where an Atlas Identity sign-in lands.
 *
 * The API has already done the whole handshake — it is the confidential client,
 * so it held the secret, exchanged the code and verified the ID token. What
 * arrives here is a single-use handoff in an **httpOnly cookie**, which this
 * page cannot read and nothing can put in a link. Claiming it yields the same
 * `{access_token, refresh_token, user}` shape every previous login path
 * returned, which is why `loginWithTokens` and all of authFetch's callers are
 * untouched by this migration.
 *
 * The handoff used to ride in the query string. It does not any more: a value
 * in the URL reaches browser history, the referrer and every proxy log, and
 * until claimed it is a bearer credential for somebody's session — so a link
 * to it would sign whoever clicked it into the account that just
 * authenticated.
 */
export default function AtlasCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithTokens, setLoggingIn } = useAuth();
  const [error, setError] = useState<string | null>(null);
  // The handoff is single-use by design, so a second POST gets a 404. React
  // StrictMode double-invokes effects in development, which would turn a
  // perfectly good sign-in into "link is no longer valid" without this.
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    // Tell ProtectedRoute a login is in flight, so it shows a spinner instead
    // of bouncing back to /login while we claim.
    setLoggingIn(true);

    (async () => {
      try {
        const res = await fetch("/api/auth/atlas/claim", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          // The handoff is in an httpOnly cookie, so the request has to carry
          // cookies and the body has nothing to say.
          credentials: "include",
          body: "{}",
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.detail || "This sign-in link is no longer valid.");
        }
        const data = await res.json();
        await loginWithTokens(data.access_token, data.refresh_token);

        const returnTo = safeReturnTo(searchParams.get("return_to"));

        if (data.needsProfile) {
          // Profile completion is what applies a pending invite's grants, so
          // it must not be skipped for an invited user. Carry the destination
          // through it, or an invited user can never finish a `cortex login`.
          navigate(
            returnTo
              ? `/complete-profile?return_to=${encodeURIComponent(returnTo)}`
              : "/complete-profile",
            { replace: true }
          );
          return;
        }
        navigate(returnTo || "/", { replace: true });
      } catch (err) {
        setLoggingIn(false);
        setError(err instanceof Error ? err.message : "Sign-in failed.");
      }
    })();
  }, [searchParams, loginWithTokens, navigate, setLoggingIn]);

  return (
    <div className="min-h-screen bg-[#f9fafb] flex items-center justify-center">
      <div className="w-full max-w-sm">
        <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-slate-200 shadow-sm p-8 text-center">
          {error === null ? (
            <>
              <Loader2 className="size-10 text-indigo-500 mx-auto mb-3 animate-spin" />
              <h2 className="text-base font-semibold text-slate-800 mb-1">Signing you in…</h2>
              <p className="text-xs text-slate-500">
                {/* Docs/Enable users have never heard of "Joveo Identity". */}
                {detectSurface().isDedicatedSurface ? "Finishing sign-in" : "Finishing up with Joveo Identity"}
              </p>
            </>
          ) : (
            <>
              <AlertCircle className="size-10 text-red-400 mx-auto mb-3" />
              <h2 className="text-base font-semibold text-slate-800 mb-1">Sign-in failed</h2>
              <p className="text-xs text-slate-500 mb-4">{error}</p>
              <Link
                to="/login"
                className="text-xs text-indigo-500 hover:text-indigo-700 font-medium"
              >
                Back to sign in
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
